import {
    Box,
    Button,
    CircularProgress,
    Divider,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type {
    VendorSubPricingDetailResponse,
    VendorSubPricingSaveLinesRequest,
    VendorSubPricingLine,
    VendorSubPricingServiceBlock,
} from '../../../models/VendorSubPricing/types'
import {
    buildCrewPriceDraftBaseline,
    findServiceBlock,
    formatSubVendorCoverageHint,
    isValidSubPricingDecimal,
    parseMoneyDisplay,
    visibleMatrixLines,
} from './subsPricingUtils'

interface Props {
    matrix: VendorSubPricingDetailResponse | null
    matrixLoading: boolean
    selectedServiceId: number | null
    selectedServiceName?: string
    workspaceLabel?: string
    crewLabel?: string
    /** Refetch/version stamp so optimistic local state resets after save. */
    detailDataUpdatedAt: number
    vendorId: number | null
    subVendorUserId: number | null
    apartmentId: number | null
    onSave: (body: VendorSubPricingSaveLinesRequest) => Promise<unknown>
    isSaving: boolean
    onDirtyChange?: (dirty: boolean) => void
}

type Variant = 'area' | 'unit'

const rowLabel = (line: VendorSubPricingLine, variant: Variant): string => {
    if (variant === 'area') {
        return line.area_config?.name ?? '—'
    }
    return line.unit_config?.name ?? '—'
}

/** Dense grid (5 cols on lg+); label above field; property→vendor caption under label. */
const PricingGridSection = (props: {
    title: string
    variant: Variant
    rows: VendorSubPricingLine[]
    emptyLabel: string
    draft: Record<number, string>
    onDraftChange: (serviceContractId: number, value: string) => void
    /** No top margin — first section in the slice or follows a Divider */
    suppressTopSpacing?: boolean
}) => {
    const theme = useTheme()
    const {
        title,
        variant,
        rows,
        emptyLabel,
        draft,
        onDraftChange,
        suppressTopSpacing = false,
    } = props

    const narrow = useMediaQuery(theme.breakpoints.down('xs'))
    const wideFive = useMediaQuery(theme.breakpoints.up('lg'))
    const gridTemplateColumns = narrow
        ? 'repeat(2, minmax(0, 1fr))'
        : wideFive
        ? 'repeat(5, minmax(0, 1fr))'
        : 'repeat(3, minmax(0, 1fr))'

    return (
        <Box
            component="section"
            marginTop={
                suppressTopSpacing ? theme.spacing(0.5) : theme.spacing(2)
            }
        >
            <Typography
                variant="subtitle2"
                style={{
                    fontWeight: 700,
                    letterSpacing: 0.25,
                    marginBottom: theme.spacing(1),
                }}
            >
                {title}
            </Typography>
            {rows.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    {emptyLabel}
                </Typography>
            ) : (
                <Box
                    style={{
                        display: 'grid',
                        gap: theme.spacing(1, 1.25),
                        gridTemplateColumns,
                    }}
                >
                    {rows.map((line, idx) => {
                        const id = line.service_contract.id
                        const raw = draft[id] ?? ''
                        const invalid =
                            raw !== '' && !isValidSubPricingDecimal(raw)
                        const pvHint = parseMoneyDisplay(
                            line.service_contract.price,
                        )
                        return (
                            <Box
                                key={`${title}-${variant}-${idx}-${id}`}
                                style={{ minWidth: 0 }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    component="label"
                                    display="block"
                                    htmlFor={`svp-${variant}-${id}`}
                                    style={{
                                        fontWeight: 700,
                                        fontSize: '1.02rem',
                                        lineHeight: 1.25,
                                        letterSpacing: -0.02,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        marginBottom: theme.spacing(0.25),
                                    }}
                                    title={rowLabel(line, variant)}
                                >
                                    {rowLabel(line, variant)}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    display="block"
                                    color="textPrimary"
                                    style={{
                                        fontWeight: 400,
                                        fontSize: '1rem',
                                        lineHeight: 1.3,
                                        marginBottom: theme.spacing(1),
                                        textAlign: 'right',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={`Property → vendor: ${pvHint}`}
                                >
                                    {pvHint}
                                </Typography>
                                <TextField
                                    id={`svp-${variant}-${id}`}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    placeholder="0.00"
                                    fullWidth
                                    value={raw}
                                    error={invalid}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    color="textSecondary"
                                                >
                                                    $
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        inputMode: 'decimal',
                                        style: {
                                            paddingTop: 8,
                                            paddingBottom: 8,
                                            textAlign: 'right',
                                        },
                                    }}
                                    onChange={(e) =>
                                        onDraftChange(id, e.target.value)
                                    }
                                />
                            </Box>
                        )
                    })}
                </Box>
            )}
        </Box>
    )
}

/** Kept so older HMR chunks / merges that still reference this name cannot crash. */
const PricingRowSection = PricingGridSection

export const SubsPricingMatrixPanel = (props: Props) => {
    const theme = useTheme()
    const {
        matrix,
        matrixLoading,
        selectedServiceId,
        selectedServiceName,
        workspaceLabel,
        crewLabel,
        detailDataUpdatedAt,
        vendorId,
        subVendorUserId,
        apartmentId,
        onSave,
        isSaving,
        onDirtyChange,
    } = props

    const [baseline, setBaseline] = useState<Record<number, string>>({})
    const [draft, setDraft] = useState<Record<number, string>>({})

    const block: VendorSubPricingServiceBlock | undefined =
        matrix && selectedServiceId != null
            ? findServiceBlock(matrix, selectedServiceId)
            : undefined

    const coverageHint = formatSubVendorCoverageHint(block)
    const coverageCaption =
        coverageHint === '—' || coverageHint === ''
            ? ''
            : `Sub-contracts on this slice: ${coverageHint}`

    useEffect(() => {
        const blk =
            matrix && selectedServiceId != null
                ? findServiceBlock(matrix, selectedServiceId)
                : undefined
        if (blk == null || (blk.areas.length === 0 && blk.units.length === 0)) {
            setBaseline({})
            setDraft({})
            return
        }
        const lines = visibleMatrixLines(blk)
        const next = buildCrewPriceDraftBaseline(lines)
        setBaseline(next)
        setDraft({ ...next })
    }, [detailDataUpdatedAt, matrix, selectedServiceId])

    const onDraftChange = useCallback(
        (serviceContractId: number, value: string) => {
            if (!isValidSubPricingDecimal(value)) return
            setDraft((prev) => ({ ...prev, [serviceContractId]: value }))
        },
        [],
    )

    const visibleLines = useMemo(() => visibleMatrixLines(block), [block])

    const isDirty = useMemo(() => {
        for (const line of visibleLines) {
            const id = line.service_contract.id
            if ((baseline[id] ?? '') !== (draft[id] ?? '')) {
                return true
            }
        }
        return false
    }, [baseline, draft, visibleLines])

    const allDraftsValid = useMemo(() => {
        for (const line of visibleLines) {
            const id = line.service_contract.id
            const v = draft[id] ?? ''
            if (!isValidSubPricingDecimal(v)) return false
        }
        return true
    }, [draft, visibleLines])

    useEffect(() => {
        onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    const handleCancel = useCallback(() => {
        setDraft({ ...baseline })
    }, [baseline])

    const handleSave = useCallback(async () => {
        if (
            vendorId == null ||
            subVendorUserId == null ||
            apartmentId == null ||
            selectedServiceId == null ||
            !allDraftsValid ||
            !isDirty
        ) {
            return
        }
        const lines = visibleLines.map((line) => {
            const id = line.service_contract.id
            const raw = (draft[id] ?? '').trim()
            return {
                service_contract_id: id,
                price: raw === '' ? null : raw,
            }
        })

        try {
            await onSave({
                vendor_id: vendorId,
                sub_vendor_user_id: subVendorUserId,
                apartment_id: apartmentId,
                service_id: selectedServiceId,
                lines,
            })
        } catch {
            /** Toast handled in mutation hook */
        }
    }, [
        allDraftsValid,
        apartmentId,
        draft,
        isDirty,
        onSave,
        selectedServiceId,
        subVendorUserId,
        vendorId,
        visibleLines,
    ])

    const canSave =
        isDirty &&
        allDraftsValid &&
        vendorId != null &&
        subVendorUserId != null &&
        apartmentId != null &&
        selectedServiceId != null &&
        !isSaving &&
        !matrixLoading

    const matrixBody = matrixLoading ? (
        <Box display="flex" justifyContent="center" padding={theme.spacing(4)}>
            <CircularProgress />
        </Box>
    ) : selectedServiceId == null ? (
        <Typography color="textSecondary">
            Select a service to view rows.
        </Typography>
    ) : !block || (block.areas.length === 0 && block.units.length === 0) ? (
        <Typography color="textSecondary">
            No contract rows returned for this service in the selected
            workspace. Other services may still have pricing lines.
        </Typography>
    ) : (
        <>
            {block.areas.length > 0 ? (
                <PricingGridSection
                    title="Area (Partial Pricing)"
                    variant="area"
                    rows={block.areas}
                    emptyLabel="No area lines."
                    suppressTopSpacing
                    draft={draft}
                    onDraftChange={onDraftChange}
                />
            ) : null}
            {block.areas.length > 0 && block.units.length > 0 ? (
                <Divider
                    style={{
                        marginTop: theme.spacing(1.75),
                        marginBottom: theme.spacing(0.5),
                    }}
                />
            ) : null}
            {block.units.length > 0 ? (
                <PricingGridSection
                    title="Unit Types (Whole Unit Pricing)"
                    variant="unit"
                    rows={block.units}
                    emptyLabel="No unit lines."
                    suppressTopSpacing
                    draft={draft}
                    onDraftChange={onDraftChange}
                />
            ) : null}
        </>
    )

    return (
        <Paper
            variant="outlined"
            style={{
                flex: 1,
                minWidth: 0,
                padding: theme.spacing(1.5),
                alignSelf: 'stretch',
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                style={{
                    gap: theme.spacing(1),
                    alignItems: 'center',
                    marginBottom: theme.spacing(0.75),
                }}
            >
                <Box minWidth={0} flex="1 1 200px">
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            marginBottom: theme.spacing(0.35),
                        }}
                    >
                        {crewLabel ?? 'Team member'}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color="textSecondary"
                        style={{
                            fontWeight: 500,
                            lineHeight: 1.3,
                            marginBottom: theme.spacing(0.25),
                        }}
                    >
                        {workspaceLabel ?? 'Workspace'}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                            lineHeight: 1.35,
                            marginBottom: theme.spacing(
                                coverageCaption ? 0.35 : 0,
                            ),
                        }}
                    >
                        {selectedServiceName ?? 'Service'}
                    </Typography>
                    {coverageCaption ? (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                        >
                            {coverageCaption}
                        </Typography>
                    ) : null}
                </Box>
                {!matrixLoading &&
                    selectedServiceId != null &&
                    block &&
                    (block.areas.length > 0 || block.units.length > 0) && (
                        <Box display="flex" alignItems="center" flexShrink={0}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCancel}
                                disabled={!isDirty || isSaving || matrixLoading}
                                style={{ marginRight: theme.spacing(1) }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => void handleSave()}
                                disabled={!canSave}
                            >
                                {isSaving ? 'Saving…' : 'Save'}
                            </Button>
                        </Box>
                    )}
            </Box>

            <Divider style={{ marginBottom: theme.spacing(1.25) }} />

            <Box>{matrixBody}</Box>
        </Paper>
    )
}
